import cx from "@hsl/core/utils/cx";

import useIsEnglishLang from "../../../hooks/useIsEnglishLang";
import { Translate } from "../../../hooks/useTranslation";
import useExplorerStore from "../../../store/store";
import { TextSkeleton } from "../../Skeletons";
import Tabs from "../../Tabs";
import TabsColumn, { type Tab } from "../../TabsColumn";
import { useDocumentContext } from "../context";
import { groupByDocType } from "../helpers/groupByDocType";
import { DocumentDisplayListWithCheckbox } from "./DocumentDisplay";
import TextButton from "./TextButton";

interface Props {
    column?: boolean;
}

const AdditionalDocumentsSection = ({ column = true }: Props) => {
    const TabComponent = column ? TabsColumn : Tabs;
    const {
        docsData: { data: docsData, isLoading },
        selectionActive,
        selectedDocumentURLs,
        addMultipleDocs,
        removeMultipleDocs,
        hasUpdates,
        markRead,
    } = useDocumentContext();
    const isEnglish = useIsEnglishLang();
    const productType = useExplorerStore((state) => state.config.productType);
    const isPMC = productType === "pmc";

    const [_firstSet, ...otherSets] = docsData ?? [];
    const setToUse = isPMC ? (_firstSet ? [_firstSet] : []) : otherSets;

    let tabs: Tab[] = setToUse.map(([set]) => ({
        key: `set-${set.set_id}`,
        label: set.display_name,
    }));
    let content = setToUse.map(([_set, docs]) => {
        const groupedDocs = Object.values(groupByDocType(docs));

        const handleSelectAll = () => {
            const docSet = groupedDocs.flatMap((docSetArr) => {
                const multipleLanguagesPresent = docSetArr.length > 1;
                return docSetArr.flatMap((doc) => ({
                    name: `${doc.language_aware_display_name}${
                        multipleLanguagesPresent && `( ${doc.language})`
                    }`,
                    value: doc.url,
                }));
            });
            const allDocsSelected = docSet.every((x) =>
                selectedDocumentURLs.some((y) => x.value === y.value),
            );
            allDocsSelected
                ? removeMultipleDocs(docSet)
                : addMultipleDocs(docSet);
        };

        return (
            <>
                {selectionActive && (
                    <div className="flex justify-end pb-2">
                        <TextButton onClick={handleSelectAll}>
                            <Translate>Select all</Translate>
                        </TextButton>
                    </div>
                )}
                <div className="pt-2 text-sm sm:pb-2">
                    <DocumentDisplayListWithCheckbox
                        data={groupedDocs}
                        selectionActive={selectionActive}
                        showLang={!isEnglish}
                        hasUpdates={hasUpdates}
                        markRead={markRead}
                    />
                </div>
            </>
        );
    });

    // if loading, otherSets will just be an empty array
    // so `tabs` and `content` will just be empty arrays
    // replace with skeletons
    if (isLoading) {
        const four = Array.from({ length: 3 }).fill(0);

        tabs = four.map((_, i) => ({
            key: `set-${i}`,
            label: (
                <>
                    <TextSkeleton width={16} className="hidden sm:block" />
                    <TextSkeleton width={12} className="sm:hidden" />
                </>
            ),
        }));
        content = four.map((_, i) => (
            <div className="flex flex-col gap-1" key={i}>
                <TextSkeleton width={20} />
                <TextSkeleton width={22} />
                <TextSkeleton width={18} />
                <TextSkeleton width={21} />
            </div>
        ));
    }

    return tabs.length > 0 ? (
        <TabComponent
            withBorder={!column}
            tabs={tabs}
            content={content}
            containerClassName={cx(isLoading && "pointer-events-none")}
            contentContainerClassName={column ? "min-w-[40%]" : ""}
        />
    ) : (
        <></>
    );
};

export default AdditionalDocumentsSection;
